// ProfileContext.tsx
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { IndRepository } from '@api/Repositories/Ind/IndRepository';
import type { Ind } from '@api/Entities/Ind/Ind';
import { useRepository } from '@app/Hooks/useRepository';

type ProfileContextValue = {
  profile: Ind | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
};

const ProfileContext = React.createContext<ProfileContextValue | undefined>(undefined);

export const ProfileProvider = ({ children }: { children: React.ReactNode }) => {
  const { profileId } = useParams<{ profileId: string }>();
  const profileIdParsed = Number(profileId);
  const individualRepository = useRepository(IndRepository);

  const [profile, setProfile] = React.useState<Ind | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<unknown>(null);

  const load = React.useCallback(() => {
    if (!profileId) return;

    setLoading(true);
    setError(null);

    individualRepository
      .getInd(profileIdParsed, { include: ['flags'] })
      .then((fetched) => {
        setProfile(fetched);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [profileId, individualRepository]);

  React.useEffect(() => {
    load();
  }, [load]);

  const value: ProfileContextValue = React.useMemo(
    () => ({ profile, loading, error, reload: load }),
    [profile, loading, error, load]
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = (): ProfileContextValue => {
  const ctx = React.useContext(ProfileContext);
  if (!ctx) {
    throw new Error('useProfile must be used within <ProfileProvider>');
  }
  return ctx;
};
